import { colors as themeColors } from '../utils/theme';
import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Card, CardContent, Grid, Switch, FormControlLabel, Button, Snackbar, Alert, Divider, Chip, CircularProgress, useTheme, useMediaQuery, Paper, Tabs, Tab, } from "@mui/material";
import { Visibility as ViewIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircleOutline as AllOnIcon, TableChart as ExcelIcon, PictureAsPdf as PdfIcon, Language as WebIcon, PhoneAndroid as MobileIcon, Business as BusinessIcon, } from "@mui/icons-material";
import modulesConfig from "../utils/modulesConfig";

const GROUP_COLORS = {
    Master:         { bg: "#eff6ff", border: "#3b82f6", header: "#1d4ed8" },
    Transaction:    { bg: "#f0fdf4", border: "#22c55e", header: "#15803d" },
    Reports:        { bg: "#fefce8", border: "#eab308", header: "#a16207" },
    Settings:       { bg: "#fdf4ff", border: "#a855f7", header: "#7e22ce" },
    UserManagement: { bg: "#fff7ed", border: "#f97316", header: "#c2410c" },
};

const PERMISSION_META = [
    { key: "view",          label: "View",  icon: <ViewIcon   sx={{ fontSize: 13 }} />, color: "#3b82f6" },
    { key: "add",           label: "Add",   icon: <AddIcon    sx={{ fontSize: 13 }} />, color: "#22c55e" },
    { key: "edit",          label: "Edit",  icon: <EditIcon   sx={{ fontSize: 13 }} />, color: themeColors.warning.main },
    { key: "delete",        label: "Del",   icon: <DeleteIcon sx={{ fontSize: 13 }} />, color: themeColors.error.main },
    { key: "downloadExcel", label: "XLS",   icon: <ExcelIcon  sx={{ fontSize: 13 }} />, color: "#16a34a" },
    { key: "viewPdf",       label: "PDF",   icon: <PdfIcon    sx={{ fontSize: 13 }} />, color: themeColors.error.main },
];

const buildInitialRights = () => {
    const initial = {};
    Object.values(modulesConfig).forEach((group) => {
        Object.keys(group.subFields).forEach((moduleKey) => {
            initial[moduleKey] = { view: false, add: false, edit: false, delete: false, downloadExcel: false, viewPdf: false };
        });
    });
    return initial;
};

const UserRightsStep = ({
    mode,
    insertedUID,
    grantedCompanies,   // [{ CompId, CompanyName }] — preferred
    companyId,          // legacy fallback when grantedCompanies not provided
    path,
    handlePrevStep,
    handleCancel,
    AppRegID,
    GCompId,
    addUserId,
    newUserCategory,    // "Admin" | "Team Leader" | "User"
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Normalize: prefer grantedCompanies, fall back to legacy companyId
    const companies = grantedCompanies?.length
        ? grantedCompanies
        : companyId
            ? [{ CompId: companyId, CompanyName: "Company" }]
            : [];

    const [activeTab, setActiveTab] = useState(0);

    // rights keyed by CompId: { [CompId]: { [moduleKey]: { view, ... } } }
    const [allRights, setAllRights] = useState(() => {
        const r = {};
        companies.forEach(c => { r[c.CompId] = buildInitialRights(); });
        return r;
    });

    const [canAccessWeb, setCanAccessWeb] = useState(true);
    const [canAccessMobile, setCanAccessMobile] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const currentCompId = companies[activeTab]?.CompId;

    // Ensure allRights has entries for all companies
    useEffect(() => {
        setAllRights(prev => {
            const next = { ...prev };
            companies.forEach(c => {
                if (!next[c.CompId]) next[c.CompId] = buildInitialRights();
            });
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies.length]);

    // Auto-grant full rights when Super Admin creates an Admin user
    useEffect(() => {
        if (mode !== "insert" || newUserCategory !== "Admin" || companies.length === 0) return;
        const fullRights = {};
        companies.forEach(c => {
            const all = {};
            Object.values(modulesConfig).forEach(g =>
                Object.keys(g.subFields).forEach(k => {
                    all[k] = { view: true, add: true, edit: true, delete: true, downloadExcel: true, viewPdf: true };
                })
            );
            fullRights[c.CompId] = all;
        });
        setAllRights(fullRights);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, newUserCategory, companies.length]);

    // Edit mode: load existing rights for all companies in parallel
    useEffect(() => {
        if (mode !== "edit" || !insertedUID || companies.length === 0) return;
        const load = async () => {
            setFetchLoading(true);
            try {
                const results = await Promise.all(
                    companies.map(c =>
                        axios.get(`${path}/api/user-rights/${insertedUID}/${c.CompId}`)
                    )
                );
                const nextRights = {};
                let webAccess = true;
                let mobileAccess = true;

                results.forEach((res, i) => {
                    const compId = companies[i].CompId;
                    nextRights[compId] = buildInitialRights();
                    if (res.data.success) {
                        res.data.data.forEach((item) => {
                            nextRights[compId][item.ModuleKey] = {
                                view:          Boolean(item.CanView),
                                add:           Boolean(item.CanAdd),
                                edit:          Boolean(item.CanEdit),
                                delete:        Boolean(item.CanDelete),
                                downloadExcel: Boolean(item.CanDownloadExcel),
                                viewPdf:       Boolean(item.CanViewPdf),
                            };
                        });
                        if (i === 0) {
                            if (res.data.canAccessWeb    !== undefined) webAccess    = Boolean(res.data.canAccessWeb);
                            if (res.data.canAccessMobile !== undefined) mobileAccess = Boolean(res.data.canAccessMobile);
                        }
                    }
                });

                setAllRights(nextRights);
                setCanAccessWeb(webAccess);
                setCanAccessMobile(mobileAccess);
            } catch (error) {
                console.error("Error fetching user rights:", error);
            } finally {
                setFetchLoading(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, insertedUID, path]);

    const currentRights = allRights[currentCompId] || buildInitialRights();

    const setModule = (moduleKey, permission, checked) => {
        setAllRights((prev) => {
            const compRights = { ...(prev[currentCompId] || buildInitialRights()) };
            const updated = { ...compRights[moduleKey], [permission]: checked };
            if (permission !== "view" && checked) updated.view = true;
            if (permission === "view" && !checked) {
                updated.add = false;
                updated.edit = false;
                updated.delete = false;
                updated.downloadExcel = false;
                updated.viewPdf = false;
            }
            return { ...prev, [currentCompId]: { ...compRights, [moduleKey]: updated } };
        });
    };

    const setGroupAll = (group, permission, checked) => {
        setAllRights((prev) => {
            const compRights = { ...(prev[currentCompId] || buildInitialRights()) };
            Object.keys(group.subFields).forEach((moduleKey) => {
                const updated = { ...compRights[moduleKey], [permission]: checked };
                if (permission !== "view" && checked) updated.view = true;
                if (permission === "view" && !checked) {
                    updated.add = false;
                    updated.edit = false;
                    updated.delete = false;
                    updated.downloadExcel = false;
                    updated.viewPdf = false;
                }
                compRights[moduleKey] = updated;
            });
            return { ...prev, [currentCompId]: compRights };
        });
    };

    const isGroupAllChecked = (group, permission) =>
        Object.keys(group.subFields).every((k) => currentRights[k]?.[permission]);

    const isGroupPartial = (group, permission) => {
        const keys = Object.keys(group.subFields);
        const count = keys.filter((k) => currentRights[k]?.[permission]).length;
        return count > 0 && count < keys.length;
    };

    const grantAllForCurrentTab = () => {
        const all = {};
        Object.values(modulesConfig).forEach((g) =>
            Object.keys(g.subFields).forEach((k) => {
                all[k] = { view: true, add: true, edit: true, delete: true, downloadExcel: true, viewPdf: true };
            })
        );
        setAllRights(prev => ({ ...prev, [currentCompId]: all }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            for (let i = 0; i < companies.length; i++) {
                const comp = companies[i];
                const compRights = allRights[comp.CompId] || buildInitialRights();
                const rights = Object.entries(compRights).map(([moduleKey, p]) => ({
                    moduleKey,
                    canView:          p.view,
                    canAdd:           p.add,
                    canEdit:          p.edit,
                    canDelete:        p.delete,
                    canDownloadExcel: p.downloadExcel,
                    canViewPdf:       p.viewPdf,
                }));
                await axios.post(`${path}/api/user-rights`, {
                    AppRegID,
                    GCompId,
                    CompId:    comp.CompId,
                    UserId:    insertedUID,
                    AddUserId: addUserId,
                    rights,
                    mode,
                    // access flags are per-user; only send on first iteration
                    ...(i === 0 ? { canAccessWeb, canAccessMobile } : {}),
                });
            }
            setSnackbar({ open: true, message: "User rights saved successfully", severity: "success" });
            setTimeout(() => handleCancel(), 1000);
        } catch (error) {
            console.error("Error saving rights:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || "Failed to save rights",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // ──────────────── Alt+S (Save) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    // Guarded by `fetchLoading`: this component early-returns to render a loading
    // skeleton instead of the real form when true, but hooks still run on every
    // render regardless — without this guard, the listener would try to save/
    // cancel a form that isn't actually showing yet.
    useEffect(() => {
        if (fetchLoading) return;

        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                if (!loading) {
                    handleSave();
                }
            } else if (e.code === "KeyC") {
                e.preventDefault();
                handleCancel();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    if (fetchLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 960, mx: "auto" }}>

            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    User Rights
                </Typography>
                <Chip
                    icon={<AllOnIcon sx={{ fontSize: 16 }} />}
                    label="Grant All"
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={grantAllForCurrentTab}
                    sx={{ cursor: "pointer" }}
                />
            </Box>

            {/* Platform Access — global per user, shown once above tabs */}
            <Paper
                variant="outlined"
                sx={{
                    mb: 2.5,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: themeColors.neutral.line,
                    bgcolor: themeColors.neutral.surfaceAlt,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                }}
            >
                <Typography fontSize={13} fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mr: 1 }}>
                    Platform Access
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={canAccessWeb}
                            onChange={(e) => setCanAccessWeb(e.target.checked)}
                            sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#1d4ed8" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#3b82f6" },
                            }}
                        />
                    }
                    label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <WebIcon sx={{ fontSize: 17, color: "#1d4ed8" }} />
                            <Typography fontSize={13} fontWeight={600} color={canAccessWeb ? "#1d4ed8" : "text.disabled"}>
                                Web
                            </Typography>
                        </Box>
                    }
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={canAccessMobile}
                            onChange={(e) => setCanAccessMobile(e.target.checked)}
                            sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#15803d" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#22c55e" },
                            }}
                        />
                    }
                    label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <MobileIcon sx={{ fontSize: 17, color: "#15803d" }} />
                            <Typography fontSize={13} fontWeight={600} color={canAccessMobile ? "#15803d" : "text.disabled"}>
                                Mobile
                            </Typography>
                        </Box>
                    }
                />

                {!canAccessWeb && (
                    <Typography fontSize={12} color="warning.main" sx={{ ml: "auto", fontStyle: "italic" }}>
                        Web login will be blocked for this user.
                    </Typography>
                )}
            </Paper>

            {/* Company Tabs — only when multiple companies granted */}
            {companies.length > 1 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ minHeight: 40 }}
                    >
                        {companies.map((c) => (
                            <Tab
                                key={c.CompId}
                                label={
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <BusinessIcon sx={{ fontSize: 14 }} />
                                        <span>{c.CompanyName}</span>
                                    </Box>
                                }
                                sx={{ minHeight: 40, fontSize: 13, textTransform: "none" }}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            {/* Module Rights Grid */}
            <Grid container spacing={2}>
                {Object.entries(modulesConfig).map(([groupKey, group]) => {
                    const colors = GROUP_COLORS[groupKey] || GROUP_COLORS.Master;
                    const allOn = PERMISSION_META.every((p) => isGroupAllChecked(group, p.key));

                    return (
                        <Grid item xs={12} md={6} key={groupKey}>
                            <Card
                                variant="outlined"
                                sx={{
                                    borderColor: colors.border,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    height: "100%",
                                }}
                            >
                                {/* Group header */}
                                <Box
                                    sx={{
                                        bgcolor: colors.bg,
                                        borderBottom: `1px solid ${colors.border}`,
                                        px: 2,
                                        py: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Typography
                                        fontWeight={700}
                                        fontSize={13}
                                        sx={{ color: colors.header, textTransform: "uppercase", letterSpacing: 0.5 }}
                                    >
                                        {group.label}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={allOn}
                                                indeterminate={
                                                    !allOn &&
                                                    PERMISSION_META.some((p) => isGroupPartial(group, p.key) || isGroupAllChecked(group, p.key))
                                                }
                                                onChange={(e) => {
                                                    const v = e.target.checked;
                                                    PERMISSION_META.forEach((p) => setGroupAll(group, p.key, v));
                                                }}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.header },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: colors.border },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography fontSize={11} color="text.secondary">
                                                All
                                            </Typography>
                                        }
                                        labelPlacement="start"
                                        sx={{ mr: 0, ml: 0 }}
                                    />
                                </Box>

                                <CardContent sx={{ p: 0 }}>
                                    {/* Permission column headers */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr repeat(6, 42px)",
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: themeColors.neutral.surfaceAlt,
                                            borderBottom: `1px solid ${themeColors.neutral.line}`,
                                        }}
                                    >
                                        <Typography fontSize={11} color="text.secondary" fontWeight={600}>
                                            Module
                                        </Typography>
                                        {PERMISSION_META.map((p) => (
                                            <Box key={p.key} textAlign="center">
                                                <Typography fontSize={11} fontWeight={600} sx={{ color: p.color }}>
                                                    {p.label}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Module rows */}
                                    {Object.entries(group.subFields).map(([moduleKey, mod], idx, arr) => (
                                        <Box key={moduleKey}>
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr repeat(6, 42px)",
                                                    alignItems: "center",
                                                    px: 2,
                                                    py: 0.5,
                                                    "&:hover": { bgcolor: themeColors.neutral.surfaceMuted },
                                                    transition: "background 0.15s",
                                                }}
                                            >
                                                <Typography fontSize={12} color="text.primary">
                                                    {mod.label}
                                                </Typography>
                                                {PERMISSION_META.map((p) => (
                                                    <Box key={p.key} display="flex" justifyContent="center">
                                                        <Switch
                                                            size="small"
                                                            checked={currentRights[moduleKey]?.[p.key] || false}
                                                            onChange={(e) => setModule(moduleKey, p.key, e.target.checked)}
                                                            sx={{
                                                                transform: "scale(0.8)",
                                                                "& .MuiSwitch-switchBase.Mui-checked": { color: p.color },
                                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                                    bgcolor: p.color,
                                                                    opacity: 0.5,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                            {idx < arr.length - 1 && <Divider sx={{ mx: 2 }} />}
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Action buttons */}
            <Box display="flex" gap={1.5} mt={3} flexDirection={isMobile ? "column" : "row"}>
                <Button variant="outlined" onClick={handlePrevStep} sx={{ flex: 1 }}>
                    Back
                </Button>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleCancel}
                    sx={{ flex: 1, color: "text.secondary" }}
                >
                    Skip & Finish
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ flex: 1 }}
                >
                    {loading ? "Saving…" : "Save Rights"}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserRightsStep;
